import { useCallback, useEffect, useState } from 'react';
import { fetchGoalSummary, fetchGoals } from '../api/endpointsBackend';

/**
 * Goals with their summaries: the list, then one summary per goal in parallel.
 * Progress is computed server side over every attached movement, in the goal's
 * own currency, so nothing here sums or converts.
 */
export function useGoalsView({ includeArchived = true, enabled = true, currency = null } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!enabled) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const goals = (await fetchGoals(includeArchived)) || [];
      // A goal keeps its own figures rather than reading as empty when the
      // display currency has no rate for it — whether the request is refused
      // outright, or comes back with nothing converted.
      const ownCurrency = (id) => fetchGoalSummary(id).catch(() => null);
      const summaries = await Promise.all(
        goals.map((g) => fetchGoalSummary(g.id, currency)
          .then((s) => (currency && s && s.num_movements > 0 && s.num_converted === 0
            ? ownCurrency(g.id)
            : s))
          .catch(() => (currency ? ownCurrency(g.id) : null))),
      );
      setRows(goals.map((g, i) => ({ ...g, summary: summaries[i] })));
    } catch (err) {
      console.error('Error loading goals:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, enabled, currency]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { rows, loading, error, refetch };
}
